/**
 * Shared auth chrome — ambient stage for admin/dev/invite login.
 * Atmosphere SSOT is the dark paint; light = photographic invert of that layer.
 * Stage is viewport-locked (`fixed inset-0` + document overflow/overscroll/bg lock)
 * so decorative layers never create page scrollbars or body letterboxing on any auth route.
 *
 * admin-marketing-cmp / GoPay §10 binary (honest):
 * Public AdminLoginShell routes set NO non-essential marketing/analytics cookies.
 * Session cookies (when AUTH_ADMIN_SESSION_COOKIE_MODE ≠ off) are essential auth only.
 * There is no marketing CMP banner here because there is nothing non-essential to consent to.
 * If a future pass adds non-essential cookies, mount a CMP on this shell before shipping.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard } from 'lucide-react';
import { resolveDirectoryMonogram, partitionLogoChipMarkStyle } from 'pi-kiosk-shared';
import {
  PLATFORM_LEGAL_FOOTER_SLUGS,
  PLATFORM_LEGAL_ROUTES,
} from '../legal/platformLegalRoutes.js';
import { brandingSignedMediaIdentity } from '../media/signedMediaUrlStability.js';
import { useStableBrandingLogoSrc } from '../media/useStableBrandingLogoSrc.js';
import { AdminBeakChevron } from './AdminBeakChevron.js';
import { AdminProfileButton } from './AdminProfileButton.js';
import { AdminProfileSheet } from './AdminProfileSheet.js';
import { AdminSettingsButton } from './AdminSettingsButton.js';
import { AdminSettingsSheet } from './AdminSettingsSheet.js';
import { HeaderLanguagePills } from './HeaderLanguagePills.js';
import { ToastProvider } from './Toast/index.js';

/** Runtime marker for audits / tests — public marketing surface claims no non-essential cookies. */
export const ADMIN_PUBLIC_NO_NON_ESSENTIAL_COOKIES = true as const;

export interface AdminLoginShellBrandMark {
  readonly name: string;
  readonly code: string;
  readonly tenantId: number;
  readonly logoSrc: string | null;
  /** Theme-resolved logo chip rim (`usePublicTenantBrand` / active theme). */
  readonly logoChipRim?: { readonly show: boolean; readonly color: string };
  /** Theme-resolved logo chip background fill behind transparent marks. */
  readonly logoChipBackground?: { readonly show: boolean; readonly color: string };
}

export interface AdminLoginShellBackNav {
  readonly to: string;
  readonly label: string;
}

export interface AdminLoginShellProps {
  readonly children: ReactNode;
  /** Optional brand line under the mark (defaults to product name). */
  readonly brandLabel?: string;
  /**
   * Tenant identity for `/{tenant}/login` chrome — logo (or monogram) + display name
   * above the auth card. When set, replaces the generic RPApp mark + brandLabel.
   */
  readonly brandMark?: AdminLoginShellBrandMark | null;
  /** Show a soft pulse while public brand is loading. */
  readonly brandMarkLoading?: boolean;
  /**
   * Optional top-left escape hatch (e.g. back to business partner directory).
   * Shown opposite Settings so users can leave a wrong tenant login.
   */
  readonly backNav?: AdminLoginShellBackNav | null;
  /**
   * When the password session is still alive on an auth recovery route
   * (e.g. OIDC link failure), show the person icon + Sign out / Account.
   */
  readonly authenticatedSession?: {
    readonly onLogout: () => void;
    readonly onOpenAccount?: () => void;
  } | null;
  readonly testId?: string;
  /**
   * Content column width.
   * - `md` — login forms (~32rem)
   * - `directory` — org picker: phone-like column on small screens, spreads on `lg+`
   */
  readonly contentSize?: 'md' | 'directory';
  /** Extra classes on the surface card (e.g. denser padding for lists). */
  readonly cardClassName?: string;
  /**
   * When false (legal docs), allow document/body scroll + print-friendly overflow.
   * Default true — auth stage locks viewport scroll.
   */
  readonly lockDocumentScroll?: boolean;
  /** Language pills in public header chrome (not Settings-only). Default true. */
  readonly showLanguagePills?: boolean;
  /** Footer links to `/legal/*`. Default true on public chrome. */
  readonly showLegalFooter?: boolean;
}

const CONTENT_MAX_CLASS: Record<'md' | 'directory', string> = {
  /**
   * Pre-auth login card track — Closed EXC≠PASS (EXC_LOGIN_BRAND_COLUMN).
   * Do not remap to --admin-content-max-width / --container-reading / --container-form as authenticated D14.
   */
  md: 'max-w-lg',
  /**
   * Pre-auth org-picker card track — Closed EXC≠PASS (EXC_LOGIN_BRAND_COLUMN).
   * Do not remap to --admin-content-max-width / --container-reading as authenticated D14.
   */
  directory: 'max-w-xl lg:max-w-4xl xl:max-w-5xl',
};

/** Matches former Tailwind `pb-10` while reserving visualViewport keyboard inset. */
const AUTH_MAIN_PADDING_BOTTOM =
  'calc(2.5rem + var(--keyboard-inset, 0px))';

/** Compact tenant mark — 56px; md+ restores 4.5rem. Soft shadow only when rim is on. */
const TENANT_MARK_CLASS =
  'relative mb-3.5 flex size-14 items-center justify-center overflow-hidden rounded-2xl bg-transparent md:mb-4 md:size-[4.5rem]';

const TENANT_MARK_FILL_CLASS =
  'absolute inset-0 flex items-center justify-center overflow-hidden rounded-2xl';

/** Skeleton must match TENANT_MARK_CLASS box so brand load does not jump the form. */
const TENANT_MARK_SKELETON_CLASS =
  'mb-3.5 size-14 animate-pulse rounded-2xl bg-zinc-900/10 dark:bg-white/15 md:mb-4 md:size-[4.5rem]';

/**
 * md+ floating-card treatment (tenant login FIX6-A target).
 * Compact omits rounded / border / heavy shadow so the form merges with the page.
 */
const TENANT_CARD_MD_FLOATING =
  'md:rounded-2xl md:border md:border-[var(--color-an-border)] md:bg-[var(--color-an-surface)] md:shadow-[0_24px_64px_-16px_rgba(15,23,42,0.18),0_8px_24px_-8px_rgba(15,23,42,0.1)] dark:md:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.04)]';

/**
 * Directory org-picker keeps a light card on compact (p-5 lg:p-8 xl:p-10).
 * Heavy floating shadow still only from md+ so the picker does not look like a login island.
 */
const DIRECTORY_CARD_SURFACE =
  'rounded-2xl border border-[var(--color-an-border)] bg-[var(--color-an-surface)] shadow-[0_8px_24px_-12px_rgba(15,23,42,0.1)] dark:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.45)] md:shadow-[0_24px_64px_-16px_rgba(15,23,42,0.18),0_8px_24px_-8px_rgba(15,23,42,0.1)] dark:md:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.04)]';

function TenantBrandHero({ brand }: { readonly brand: AdminLoginShellBrandMark }): JSX.Element {
  const logoSrc = brand.logoSrc !== null && brand.logoSrc.length > 0 ? brand.logoSrc : null;
  const { displaySrc, displayKey } = useStableBrandingLogoSrc(logoSrc);
  const [failedLogoKey, setFailedLogoKey] = useState<string | null>(null);
  const logoFailed = failedLogoKey !== null && failedLogoKey === displayKey;
  const monogram = resolveDirectoryMonogram({
    id: brand.tenantId,
    displayName: brand.name,
    code: brand.code,
  });
  const showLogo = displaySrc !== null && !logoFailed;
  const showRim = brand.logoChipRim?.show === true;
  const rimColor = brand.logoChipRim?.color;
  const showBackground = brand.logoChipBackground?.show === true;
  const backgroundColor = brand.logoChipBackground?.color;

  const chipStyle = partitionLogoChipMarkStyle(
    {
      rim: { show: showRim && rimColor !== undefined, color: rimColor ?? '' },
      background: {
        show: showBackground && backgroundColor !== undefined,
        color: backgroundColor ?? '',
      },
    },
    'loginHero',
  );

  const markBody = showLogo ? (
    <img
      key={displayKey ?? displaySrc}
      src={displaySrc}
      alt=""
      className="h-full w-full object-contain"
      loading="eager"
      decoding="async"
      onError={() => {
        setFailedLogoKey(brandingSignedMediaIdentity(displaySrc) ?? displaySrc);
      }}
    />
  ) : (
    <span
      className="flex h-full w-full items-center justify-center text-xl font-semibold uppercase tracking-tight md:text-2xl"
      style={{
        backgroundColor: showBackground ? 'transparent' : monogram.backgroundColor,
        color: monogram.textColor,
      }}
    >
      {monogram.label}
    </span>
  );

  return (
    <div className="flex flex-col items-center text-center" data-testid="admin-login-tenant-brand">
      <div
        className={TENANT_MARK_CLASS}
        style={chipStyle.shell as CSSProperties | undefined}
        data-logo-chip-rim={showRim ? 'true' : 'false'}
        data-logo-chip-background={showBackground ? 'true' : 'false'}
        data-testid="admin-login-tenant-mark"
      >
        {chipStyle.fill != null ? (
          <span
            className={TENANT_MARK_FILL_CLASS}
            style={chipStyle.fill as CSSProperties}
            data-logo-chip-fill="true"
            data-testid="admin-login-tenant-mark-fill"
          >
            {markBody}
          </span>
        ) : (
          markBody
        )}
      </div>
      {/*
        G7 / EXC-LOGIN-BRAND-COLUMN: pre-auth brand label column (≠ content track).
        Do not remap to --admin-content-max-width / D14 containers — Closed EXC.
      */}
      <p className="m-0 max-w-[18rem] text-lg font-semibold tracking-tight text-zinc-900 dark:text-[#fafafa] md:max-w-sm md:text-xl">
        {brand.name}
      </p>
    </div>
  );
}

function BrandHeroSkeleton(): JSX.Element {
  return (
    <div
      className="flex flex-col items-center text-center"
      aria-busy
      data-testid="admin-login-tenant-brand-loading"
    >
      <div className={TENANT_MARK_SKELETON_CLASS} />
      <div className="h-6 w-40 animate-pulse rounded bg-zinc-900/10 dark:bg-white/15 md:h-7 md:w-48" />
    </div>
  );
}

function DefaultBrandHero({ brandLabel }: { readonly brandLabel: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center text-center" data-testid="admin-login-default-brand">
      <div className="mb-3 flex items-center justify-center md:mb-4">
        <div className="flex size-14 items-center justify-center rounded-2xl border border-zinc-900/15 bg-zinc-100 text-zinc-900 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)] dark:border-white/25 dark:bg-[#27272a] dark:text-[#fafafa] dark:shadow-[0_4px_16px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.08)] md:size-16 md:shadow-[0_12px_40px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.04)] dark:md:shadow-[0_12px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.08)]">
          <LayoutDashboard className="size-7 stroke-[1.75] md:size-8" aria-hidden />
        </div>
      </div>
      <p className="m-0 text-[length:var(--font-size-small)] font-[number:var(--font-weight-semibold)] tracking-[0.04em] text-zinc-600 dark:text-zinc-300">
        {brandLabel}
      </p>
    </div>
  );
}

export function AdminLoginShell({
  children,
  brandLabel = '',
  brandMark = null,
  brandMarkLoading = false,
  backNav = null,
  authenticatedSession = null,
  testId = 'admin-login-screen',
  contentSize = 'md',
  cardClassName,
  lockDocumentScroll = true,
  showLanguagePills = true,
  showLegalFooter = true,
}: AdminLoginShellProps): JSX.Element {
  const { t } = useTranslation('admin');
  const headerRef = useRef<HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);
  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);
  const openProfile = useCallback(() => {
    setProfileOpen(true);
  }, []);
  const closeProfile = useCallback(() => {
    setProfileOpen(false);
  }, []);

  // Fix 7 G12 / AC-04: Settings/Profile DetailDrawers anchor under
  // `--admin-header-compact-height`. Publish LIVE login-header offsetHeight
  // (already includes safe-area) so sheets use the var only — no double SA.
  // Mirror EnterprisePageHeader; restore prior value on unmount.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (el === null) {
      return undefined;
    }
    const root = document.documentElement;
    const previous = root.style.getPropertyValue('--admin-header-compact-height');
    const sync = (): void => {
      root.style.setProperty('--admin-header-compact-height', `${String(el.offsetHeight)}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (previous === '') {
        root.style.removeProperty('--admin-header-compact-height');
      } else {
        root.style.setProperty('--admin-header-compact-height', previous);
      }
    };
  }, []);

  // Mirror EnterpriseShell: keep focused login fields above the virtual keyboard.
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;
    const updateKeyboardInset = (): void => {
      if (vv === null || vv === undefined) {
        root.style.setProperty('--keyboard-inset', '0px');
        return;
      }
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--keyboard-inset', `${String(inset)}px`);
    };
    updateKeyboardInset();
    vv?.addEventListener('resize', updateKeyboardInset);
    vv?.addEventListener('scroll', updateKeyboardInset);
    return () => {
      vv?.removeEventListener('resize', updateKeyboardInset);
      vv?.removeEventListener('scroll', updateKeyboardInset);
      root.style.removeProperty('--keyboard-inset');
    };
  }, []);

  // Lock document scroll/overscroll + align document paint with the auth stage.
  // Legal routes set lockDocumentScroll=false for scroll/print-friendly reading.
  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const appRoot = document.getElementById('root');

    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverscroll = html.style.overscrollBehavior;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlBackground = html.style.backgroundColor;
    const previousBodyBackground = body.style.backgroundColor;
    const previousRootBackground = appRoot?.style.backgroundColor ?? '';

    if (lockDocumentScroll) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.overscrollBehavior = 'none';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      html.style.overscrollBehavior = 'auto';
      body.style.overscrollBehavior = 'auto';
    }
    // Same token as EnterpriseShell / theme-runtime — no #050505 vs #09090b split.
    html.style.backgroundColor = 'var(--color-an-bg)';
    body.style.backgroundColor = 'var(--color-an-bg)';
    if (appRoot !== null) {
      appRoot.style.backgroundColor = 'var(--color-an-bg)';
    }

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      html.style.overscrollBehavior = previousHtmlOverscroll;
      body.style.overscrollBehavior = previousBodyOverscroll;
      html.style.backgroundColor = previousHtmlBackground;
      body.style.backgroundColor = previousBodyBackground;
      if (appRoot !== null) {
        appRoot.style.backgroundColor = previousRootBackground;
      }
    };
  }, [lockDocumentScroll]);

  const isDirectory = contentSize === 'directory';
  const cardClasses = [
    'text-[var(--color-an-text)]',
    isDirectory ? DIRECTORY_CARD_SURFACE : TENANT_CARD_MD_FLOATING,
    isDirectory ? 'p-5 lg:p-8 xl:p-10' : 'p-5 md:p-10',
    cardClassName,
  ]
    .filter(Boolean)
    .join(' ');

  let hero: ReactNode;
  if (brandMarkLoading && brandMark === null) {
    hero = <BrandHeroSkeleton />;
  } else if (brandMark !== null) {
    hero = <TenantBrandHero brand={brandMark} />;
  } else {
    hero = <DefaultBrandHero brandLabel={brandLabel} />;
  }

  const footerRoutes = PLATFORM_LEGAL_ROUTES.filter((route) =>
    PLATFORM_LEGAL_FOOTER_SLUGS.includes(route.slug),
  );

  const stageClass = lockDocumentScroll
    ? 'fixed inset-0 flex flex-col overflow-hidden bg-[var(--color-an-bg)] text-[var(--color-an-text)] print:static print:min-h-0 print:overflow-visible'
    : 'relative flex min-h-[100dvh] flex-col overflow-x-hidden overflow-y-auto bg-[var(--color-an-bg)] text-[var(--color-an-text)] print:overflow-visible';

  return (
    <ToastProvider>
      <div
        className={stageClass}
        data-testid={testId}
        data-login-layout="unified"
        data-document-scroll={lockDocumentScroll ? 'locked' : 'page'}
      >
        <div aria-hidden="true" className="admin-login-shell__atmosphere print:hidden" />

        {/*
          FIX6 G9: compact chrome band ~52–56px (min-h-14); safe-area padding is ADDITIVE.
          Do not use h-14 max-h-14 + bare pt-[var(--safe-area-inset-top)] — that caps the
          border-box at 56px and squeezes ≥44px controls when inset is non-zero.

          Overlay chrome (absolute): the scroll column fills the stage under the header so
          brand/card are not clipped at the header/main seam. Interactive controls stay
          above content (`z-[3]` + pointer-events-auto); the empty band lets content show
          through so the mark is never “hidden under the top bar”.
        */}
        <header
          ref={headerRef}
          className="pointer-events-none absolute inset-x-0 top-0 z-[3] flex min-h-14 items-center justify-between gap-3 px-4 pt-[max(0px,var(--safe-area-inset-top))] md:h-auto md:px-6 md:py-4 md:pt-4 print:static print:pointer-events-auto"
        >
          <div
            className="pointer-events-auto flex min-w-0 flex-1 items-center gap-2"
            aria-hidden={backNav == null && !showLanguagePills ? true : undefined}
          >
            {showLanguagePills ? (
              <HeaderLanguagePills
                placement="header"
                dataTestId="admin-login-language"
              />
            ) : null}
          </div>
          <div className="pointer-events-auto flex shrink-0 items-center justify-end gap-1 sm:gap-2 print:hidden">
            {backNav != null ? (
              <Link
                to={backNav.to}
                className="inline-flex h-11 min-h-11 max-w-full items-center gap-1.5 rounded-full px-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-900/5 hover:text-zinc-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-zinc-500 dark:text-zinc-300 dark:hover:bg-white/10 dark:hover:text-white dark:focus-visible:outline-zinc-400"
                data-testid="admin-login-back-to-organizations"
              >
                <AdminBeakChevron direction="left" className="h-5 w-5 shrink-0" />
                <span className="truncate">{backNav.label}</span>
              </Link>
            ) : null}
            <AdminSettingsButton open={settingsOpen} onOpen={openSettings} />
            {authenticatedSession != null ? (
              <AdminProfileButton onClick={openProfile} open={profileOpen} />
            ) : null}
          </div>
        </header>

        <AdminSettingsSheet open={settingsOpen} onClose={closeSettings} />
        {authenticatedSession != null ? (
          <AdminProfileSheet
            open={profileOpen}
            onClose={closeProfile}
            onLogout={() => {
              closeProfile();
              authenticatedSession.onLogout();
            }}
            onOpenAccount={
              authenticatedSession.onOpenAccount != null
                ? () => {
                    closeProfile();
                    authenticatedSession.onOpenAccount?.();
                  }
                : undefined
            }
          />
        ) : null}

        {/*
          Full-stage scroll column under overlay chrome. Brand + card stay top-aligned;
          no my-auto / justify-center (those clip tops). Padding clears the live header
          height so Settings stays usable; content may paint through the chrome band.
        */}
        <main
          className={[
            'admin-scrollbar relative z-[2] min-h-0 flex-1 overflow-x-hidden overscroll-contain px-4 pt-[calc(var(--admin-header-compact-height,3.5rem)+0.75rem)] sm:pt-[calc(var(--admin-header-compact-height,3.5rem)+1rem)] md:px-6 md:pt-[calc(var(--admin-header-compact-height,3.5rem)+1.5rem)] lg:px-10',
            lockDocumentScroll ? 'overflow-y-auto' : 'overflow-y-visible',
            'print:overflow-visible print:pt-4',
          ].join(' ')}
          data-auth-stage-layout="unified-start"
          style={{ paddingBottom: AUTH_MAIN_PADDING_BOTTOM }}
        >
          <div
            className={`relative z-[1] mx-auto w-full min-w-0 ${CONTENT_MAX_CLASS[contentSize]}`}
            data-testid="admin-login-auth-column"
          >
            <div className={`relative z-[2] ${isDirectory ? 'mb-6 lg:mb-9' : 'mb-4 md:mb-8'}`}>
              {hero}
            </div>
            <div className={`relative z-[1] ${cardClasses}`}>{children}</div>
            {showLegalFooter ? (
              <div className="relative z-[1] mt-6 flex flex-col items-center gap-2 pb-2 print:mt-4">
                <p
                  className="m-0 max-w-prose text-center text-[0.7rem] leading-snug text-[var(--color-an-text-muted)]"
                  data-testid="admin-login-no-non-essential-cookies"
                  data-admin-cmp="none-required"
                >
                  {t('legal.cmp.noNonEssentialCookies', {
                    defaultValue:
                      'This public surface does not use non-essential cookies. Essential session cookies only when you sign in.',
                  })}
                </p>
                <nav
                  className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-xs text-[var(--color-an-text-muted)]"
                  aria-label={t('legal.footer.aria', {
                    defaultValue: 'Právní dokumenty',
                  })}
                  data-testid="admin-login-legal-footer"
                >
                  {footerRoutes.map((route) => (
                    <Link
                      key={route.slug}
                      to={route.path}
                      className="underline underline-offset-2 hover:text-[var(--color-an-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-primary)]"
                      data-testid={`admin-login-legal-footer-${route.slug}`}
                    >
                      {t(route.footerKey, { defaultValue: route.slug })}
                    </Link>
                  ))}
                </nav>
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </ToastProvider>
  );
}
